import { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Hls from 'hls.js'
import SpeakToExpertButton from '../../../components/home/SpeakToExpertButton'
import { SpeakerIcon, SpeakerMutedIcon } from '../../../components/home/VideoIcons'
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver'
import { getHeroVideoSources, initHLSOnVideo } from '../../../utils/videoUtils'

const heroVideoSources = getHeroVideoSources()

const HeroSection = () => {
    const { t } = useTranslation('home')
    const { t: tCommon } = useTranslation('common')
    const mobileVideoRef = useRef<HTMLVideoElement>(null)
    const desktopVideoRef = useRef<HTMLVideoElement>(null)
    const mobileHlsRef = useRef<Hls | null>(null)
    const desktopHlsRef = useRef<Hls | null>(null)
    const [mobileVideoLoaded, setMobileVideoLoaded] = useState(false)
    const [desktopVideoLoaded, setDesktopVideoLoaded] = useState(false)
    const [isMuted, setIsMuted] = useState(true)

    const toggleMute = useCallback(() => {
        setIsMuted((prev) => {
            const next = !prev
            if (mobileVideoRef.current) mobileVideoRef.current.muted = next
            if (desktopVideoRef.current) desktopVideoRef.current.muted = next
            return next
        })
    }, [])

    // Intersection observer for mobile hero section
    const { ref: mobileRef, isVisible: mobileIsVisible } = useIntersectionObserver<HTMLElement>({
        threshold: 0.1,
        rootMargin: '100px',
    })

    // Intersection observer for desktop hero section
    const { ref: desktopRef, isVisible: desktopIsVisible } = useIntersectionObserver<HTMLElement>({
        threshold: 0.1,
        rootMargin: '100px',
    })

    // Cleanup HLS instances on unmount
    useEffect(() => {
        return () => {
            mobileHlsRef.current?.destroy()
            desktopHlsRef.current?.destroy()
        }
    }, [])

    // Load and play mobile video when visible
    useEffect(() => {
        const video = mobileVideoRef.current
        if (!video) return

        if (mobileIsVisible && !mobileVideoLoaded) {
            // Load video source with HLS
            mobileHlsRef.current = initHLSOnVideo(video, heroVideoSources)
            setMobileVideoLoaded(true)
        }

        if (mobileIsVisible && mobileVideoLoaded) {
            video.play().catch((e) => console.debug('Mobile video play prevented:', e))
        } else if (!mobileIsVisible && mobileVideoLoaded) {
            video.pause()
        }
    }, [mobileIsVisible, mobileVideoLoaded])

    // Load and play desktop video when visible
    useEffect(() => {
        const video = desktopVideoRef.current
        if (!video) return

        if (desktopIsVisible && !desktopVideoLoaded) {
            // Load video source with HLS
            desktopHlsRef.current = initHLSOnVideo(video, heroVideoSources)
            setDesktopVideoLoaded(true)
        }

        if (desktopIsVisible && desktopVideoLoaded) {
            video.play().catch((e) => console.debug('Desktop video play prevented:', e))
        } else if (!desktopIsVisible && desktopVideoLoaded) {
            video.pause()
        }
    }, [desktopIsVisible, desktopVideoLoaded])

    return (
        <>
            <section ref={mobileRef} className="relative h-dvh sm:hidden">
                <video
                    ref={mobileVideoRef}
                    poster={heroVideoSources.poster}
                    muted
                    loop
                    playsInline
                    preload="none"
                    className="absolute inset-0 w-full h-full object-cover"
                />

                <div className="absolute inset-0 bg-black/50" />

                <button
                    onClick={toggleMute}
                    aria-label={isMuted ? tCommon('aria.unmuteVideo') : tCommon('aria.muteVideo')}
                    className="absolute bottom-6 left-4 z-20 flex items-center justify-center w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 transition-colors hover:bg-black/60"
                >
                    {isMuted ? <SpeakerMutedIcon /> : <SpeakerIcon />}
                </button>

                <div className="absolute inset-0 z-10 flex flex-col px-4 justify-center">
                    <h1 className="text-[26px] font-semibold leading-tight text-white uppercase">
                        {t('hero.title')}
                    </h1>

                    <p className="text-sm font-medium leading-tight text-white mt-4 max-w-[270px]">
                        {t('hero.subtitleMobile')}
                    </p>

                    <SpeakToExpertButton className="mt-8" />
                </div>
            </section>

            <section ref={desktopRef} className="hidden sm:block pt-12 lg:!py-12">
                <div className="max-w-[1340px] mx-auto flex flex-col xl:flex-row gap-y-10 gap-x-24 items-center justify-between px-10">
                    <div className="w-full max-w-[524px] lg:max-w-full xl:max-w-[618px] xl:shrink-0 text-center xl:text-left">
                        <h1 className="text-2xl lg:text-[44px] font-semibold leading-tight text-black uppercase">
                            {t('hero.title')}
                        </h1>

                        <p className="text-base lg:text-xl tracking-wider leading-tight text-black mt-4 xl:mt-6 !font-normal">
                            {t('hero.subtitleDesktop')}
                        </p>

                        <SpeakToExpertButton className="mt-10 xl:mt-6" />
                    </div>

                    <div className="relative shrink-0 lg:pr-9">
                        <div
                            className="absolute left-0 top-[25px] bottom-[25px] lg:top-[37.5px] lg:bottom-[37.5px] w-[calc(100%+26px)] lg:w-full rounded-[20.31px] lg:rounded-[30px] bg-[#EFE8DF]"
                        />

                        <video
                            ref={desktopVideoRef}
                            poster={heroVideoSources.poster}
                            muted
                            loop
                            playsInline
                            preload="none"
                            className="relative z-10 w-[319px] lg:w-[472px] aspect-[472/740] object-cover rounded-[20.31px] lg:rounded-[30px]"
                        />

                        <button
                            onClick={toggleMute}
                            aria-label={isMuted ? tCommon('aria.unmuteVideo') : tCommon('aria.muteVideo')}
                            className="absolute bottom-[38px] lg:bottom-10 right-[4rem] z-20 flex items-center justify-center w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 transition-colors hover:bg-black/60"
                        >
                            {isMuted ? <SpeakerMutedIcon /> : <SpeakerIcon />}
                        </button>
                    </div>
                </div>
            </section>
        </>
    )
}

export default HeroSection
